import * as React from "react";
import { ExpandableFormSection, Summary, FormSectionHeading, SensitiveFileUpload } from "components/form";
import { AccountType, GoogleCloudAccountResource } from "client/resources/accountResource";
import AccountEditBase, { AccountEditModel } from "./AccountEditBase";
import { SensitiveValue } from "client/resources/propertyValueResource";
import { ObfuscatedPlaceholder } from "components/form/Sensitive/Sensitive";
import { ActionButton } from "components/Button/ActionButton";
import SaveAndTestAccountDialog, { SaveAndTestAccountProps } from "./SaveAndTestAccountDialog";
import Dialog from "components/Dialog/Dialog";
import { repository } from "../../../../clientInstance";
import ExternalLink from "../../../../components/Navigation/ExternalLink";
import Note from "../../../../primitiveComponents/form/Note/Note";

interface GoogleCloudAccountModel extends AccountEditModel {
    jsonKey: SensitiveValue;
}

class GoogleCloudAccountEdit extends AccountEditBase<GoogleCloudAccountResource, GoogleCloudAccountModel> {
    getPartialModel(account?: GoogleCloudAccountResource): Partial<GoogleCloudAccountModel> {
        if (!account) {
            return {
                jsonKey: {
                    HasValue: false,
                },
            };
        }

        return {
            jsonKey: account.JsonKey,
        };
    }

    getPartialResource(): (Partial<GoogleCloudAccountResource> & { AccountType: AccountType }) | undefined {
        return {
            AccountType: AccountType.GoogleCloudAccount,
            JsonKey: this.state.model.jsonKey,
        };
    }

    jsonKeySummary() {
        return this.state.model.jsonKey && this.state.model.jsonKey.HasValue ? Summary.summary(ObfuscatedPlaceholder) : Summary.placeholder("No key file provided");
    }

    customSecondaryAction(): React.ReactElement {
        return (
            <div>
                <ActionButton onClick={() => this.handleSaveClick(true)} label="Save and Test" />
                {this.state.accountData !== undefined && (
                    <Dialog open={this.state.showTestDialog}>
                        <SaveAndTestGoogleCloudAccountDialog onOkClick={() => this.testDone()} accountId={this.state.accountData.account.Id} />
                    </Dialog>
                )}
            </div>
        );
    }

    customExpandableFormSections(): React.ReactElement[] {
        const baseElements: React.ReactElement[] = [];

        baseElements.push(
            <FormSectionHeading title="Credentials" key={"header"} />,
            <ExpandableFormSection key="keyFile" errorKey="keyFile" title="Key File" summary={this.jsonKeySummary()} help="The json key file to use when authenticating against Google Cloud.">
                <SensitiveFileUpload value={this.state.model.jsonKey} onChange={(jsonKey) => this.setModelState({ jsonKey })} label="Key File" error={this.getFieldError("KeyFile")} />
                <Note>
                    Refer to the <ExternalLink href="GoogleCloudServiceAccountKey">Google cloud documentation</ExternalLink> for information on generating json key for service accounts.
                </Note>
            </ExpandableFormSection>
        );

        return baseElements;
    }
}

const SaveAndTestGoogleCloudAccountDialog: React.SFC<SaveAndTestAccountProps> = ({ accountId, onOkClick }) => {
    return <SaveAndTestAccountDialog accountId={accountId} onOkClick={onOkClick} createTask={(accId) => repository.Tasks.createTestGoogleCloudAccountTask(accId)} />;
};

export default GoogleCloudAccountEdit;
